import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Building2, FileText, Mail, MapPin, Save, Loader2, RefreshCw } from 'lucide-react';
import { useOrgFeatures } from '../hooks/useOrgFeatures';

const EU_COUNTRIES = [
  { code: 'ES', name: 'España' },
  { code: 'DE', name: 'Alemania' },
  { code: 'FR', name: 'Francia' },
  { code: 'IT', name: 'Italia' },
  { code: 'PT', name: 'Portugal' },
  { code: 'NL', name: 'Países Bajos' },
  { code: 'BE', name: 'Bélgica' },
  { code: 'PL', name: 'Polonia' },
  { code: 'SE', name: 'Suecia' },
  { code: 'AT', name: 'Austria' },
  { code: 'DK', name: 'Dinamarca' },
  { code: 'FI', name: 'Finlandia' },
  { code: 'GB', name: 'Reino Unido' },
  { code: 'MX', name: 'México' },
  { code: 'US', name: 'Estados Unidos' },
  { code: 'OTHER', name: 'Otro' },
];

function BillingDetailsForm() {
  const empty = { company_name: '', tax_id: '', billing_email: '', address_line1: '', city: '', postal_code: '', country: 'ES' };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/billing/billing-details')
      .then(r => setForm({ ...empty, ...r.data }))
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put('/api/billing/billing-details', form);
      if (r.data.ok) {
        toast.success(r.data.stripe_synced
          ? 'Datos de facturación guardados y sincronizados con Stripe'
          : 'Datos guardados (sincronización Stripe pendiente)'
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-8 animate-pulse">
        <div className="h-4 bg-gray-800 rounded w-48 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-10 bg-gray-800 rounded" />)}
        </div>
      </div>
    );
  }

  const field = (label, key, type = 'text', placeholder = '') => (
    <div>
      <label className="block text-xs text-gray-400 mb-1.5">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        placeholder={placeholder}
        data-testid={`billing-field-${key}`}
        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-indigo-500 transition-colors"
      />
    </div>
  );

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-8">
      <div className="flex items-center gap-2 mb-5">
        <FileText size={18} className="text-indigo-400" />
        <h2 className="text-white font-semibold">Datos de facturación</h2>
        <span className="text-xs text-gray-500 ml-1">· aparecerán en tus facturas de Stripe</span>
      </div>

      <form onSubmit={handleSave} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            {field('Razón social', 'company_name', 'text', 'Nombre de tu empresa')}
          </div>
          {field('NIF / CIF / VAT', 'tax_id', 'text', 'B12345678 o ESB12345678')}
          {field('Email de facturación', 'billing_email', 'email', 'facturas@tuempresa.com')}
          <div className="md:col-span-2">
            {field('Dirección', 'address_line1', 'text', 'Calle, número, piso...')}
          </div>
          {field('Ciudad', 'city', 'text', 'Madrid')}
          {field('Código postal', 'postal_code', 'text', '28001')}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">País</label>
            <select
              value={form.country}
              onChange={e => set('country', e.target.value)}
              data-testid="billing-field-country"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
            >
              {EU_COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={saving}
            data-testid="billing-details-save-btn"
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {saving ? 'Guardando...' : 'Guardar datos fiscales'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Colores y "popular" por plan_key (fallback neutro para planes custom)
const PLAN_META = {
  search_basic:   { color: 'blue',   popular: false },
  search_ai:      { color: 'indigo', popular: false },
  search_ai_pro:  { color: 'purple', popular: false },
  chat:           { color: 'emerald',popular: false },
  full:           { color: 'violet', popular: true  },
};

const COLOR_CLASSES = {
  blue:    { border: 'border-blue-500/50',    btn: 'bg-blue-600 hover:bg-blue-700',       badge: 'bg-blue-600' },
  indigo:  { border: 'border-indigo-500/50',  btn: 'bg-indigo-600 hover:bg-indigo-700',   badge: 'bg-indigo-600' },
  purple:  { border: 'border-purple-500/50',  btn: 'bg-purple-600 hover:bg-purple-700',   badge: 'bg-purple-600' },
  emerald: { border: 'border-emerald-500/50', btn: 'bg-emerald-600 hover:bg-emerald-700', badge: 'bg-emerald-600' },
  violet:  { border: 'border-violet-500/50',  btn: 'bg-violet-600 hover:bg-violet-700',   badge: 'bg-violet-600' },
  gray:    { border: 'border-gray-700',        btn: 'bg-gray-600 hover:bg-gray-500',       badge: 'bg-gray-600' },
};

const fmtPrice = (price) => {
  const n = Number(price || 0);
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(2).replace('.', ',');
};

function planFeaturesToList(features = {}) {
  const items = [];
  if (features.classic_search) items.push('Búsqueda clásica multiidioma');
  if (features.ai_enabled) {
    const lim = features.ai_searches_limit;
    items.push(lim ? `IA semántica (${Number(lim).toLocaleString()}/mes)` : 'IA semántica ilimitada');
  }
  if (features.chat_enabled && features.chat_ai_enabled) items.push('Chat IA (bot + agentes)');
  else if (features.chat_enabled) items.push('Chat con agentes humanos');
  if (features.whatsapp_enabled) items.push('WhatsApp Omnichannel');
  if (features.customer_lookup_enabled) items.push('Consulta de pedidos');
  if (features.reviews_enabled) items.push('Módulo de Opiniones');
  if (features.xml_feed) items.push('Feed XML / PrestaShop / Shopify');
  if (features.analytics) items.push('Analytics de búsquedas');
  if (features.widget_customization) items.push('Widget personalizable');
  if (features.agents_limit) items.push(`Hasta ${features.agents_limit} agente${features.agents_limit !== 1 ? 's' : ''}`);
  return items;
}

export default function BillingPage() {
  const { refresh: refreshFeatures } = useOrgFeatures();
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [addonQuantities, setAddonQuantities] = useState({});  // { plan_key: quantity }
  const [plansLoading, setPlansLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null); // feedback tras volver de Stripe
  const [reconciling, setReconciling] = useState(false);
  const [pendingChange, setPendingChange] = useState(null); // { plan, preview } para el modal
  const [changeLoading, setChangeLoading] = useState(false); // aplicando cambio
  const [previewLoading, setPreviewLoading] = useState(null); // plan_key en preview

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get('/api/billing/subscription-status');
      setStatus(r.data);
    } catch {}
  }, []);

  // Reconcilia el plan directamente desde la suscripción real de Stripe.
  // Independiente del webhook: si el webhook falla, esto repara el plan igualmente.
  const reconcile = useCallback(async ({ silent = true } = {}) => {
    setReconciling(true);
    try {
      const r = await api.post('/api/billing/reconcile');
      await loadStatus();
      if (r.data?.changed) {
        await refreshFeatures();
        if (!silent) toast.success(`Plan sincronizado: ${r.data.plan_key}`);
        return true;
      }
      if (!silent) {
        if (r.data?.reconciled) toast.success('Tu plan ya estaba sincronizado.');
        else if (r.data?.reason === 'no_active_subscription') toast.info('No hay ninguna suscripción activa en Stripe.');
        else if (r.data?.reason === 'no_customer') toast.info('Aún no tienes cuenta de facturación. Suscríbete primero.');
        else if (r.data?.reason === 'plan_not_resolved') toast.warning('Suscripción encontrada pero no se pudo identificar el plan. Contacta soporte.');
        else toast.info('Sin cambios.');
      }
      return false;
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || 'No se pudo sincronizar con Stripe.');
      return false;
    } finally {
      setReconciling(false);
    }
  }, [loadStatus, refreshFeatures]);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const r = await api.get('/api/billing/plans');
      setPlans((r.data.plans || []).filter(p => !p.is_addon));
      setAddons((r.data.plans || []).filter(p => !!p.is_addon && p.plan_key !== 'onboarding'));
    } catch {}
    finally { setPlansLoading(false); }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const canceled = params.get('canceled');

    // Limpiar query string de la URL sin recargar la página
    if (sessionId || canceled) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (canceled) {
      setSyncMessage({ type: 'warn', text: 'Pago cancelado. No se realizó ningún cargo.' });
    }

    const init = async () => {
      await Promise.all([loadPlans(), loadStatus()]);
      setLoading(false);

      if (sessionId) {
        setSyncMessage({ type: 'info', text: 'Verificando tu pago con Stripe...' });
        try {
          const r = await api.get(`/api/billing/checkout-status/${sessionId}`);
          if (r.data.status === 'complete' && r.data.payment_status === 'paid') {
            await reconcile({ silent: true }); // asegura plan correcto desde la suscripción real
            await loadStatus();
            setSyncMessage({ type: 'success', text: '¡Pago confirmado! Tu plan ha sido actualizado.' });
          } else if (r.data.status === 'open') {
            setSyncMessage({ type: 'warn', text: 'El pago aún no se ha completado. Comprueba tu email.' });
          } else {
            setSyncMessage({ type: 'info', text: 'Pago procesado. Si el plan no se actualiza en unos minutos, recarga la página.' });
          }
        } catch {
          setSyncMessage({ type: 'warn', text: 'No se pudo verificar el pago automáticamente. Si pagaste correctamente, el plan se actualizará en breve.' });
        }
      } else {
        // Auto-reconciliación silenciosa al entrar: repara el plan aunque el webhook haya fallado.
        reconcile({ silent: true });
      }
    };

    init();
  }, [loadPlans, loadStatus, reconcile]);

  const handleCheckout = async (planKey, quantity = 1) => {
    setCheckoutLoading(planKey);
    try {
      const r = await api.post('/api/billing/create-checkout-session', {
        plan_key: planKey,
        origin_url: window.location.origin,
        quantity: Math.max(1, Math.min(999, Number(quantity) || 1)),
      });
      window.location.href = r.data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al iniciar el pago');
      setCheckoutLoading(null);
    }
  };

  const handlePortal = async () => {
    try {
      const r = await api.post('/api/billing/create-portal-session', {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al abrir el portal');
    }
  };

  // Selección de un plan en la rejilla:
  // - Sin suscripción activa → Checkout (alta nueva).
  // - Con suscripción activa → cambio con prorrateo (previsualiza importe y confirma).
  const handleSelectPlan = async (plan) => {
    if (!isActive) {
      return handleCheckout(plan.plan_key);
    }
    setPreviewLoading(plan.plan_key);
    try {
      const r = await api.post('/api/billing/change-plan/preview', { plan_key: plan.plan_key });
      setPendingChange({ plan, preview: r.data });
    } catch (e) {
      // Si el preview falla, igual permitimos confirmar el cambio (sin importe exacto).
      setPendingChange({ plan, preview: { available: false } });
    } finally {
      setPreviewLoading(null);
    }
  };

  const confirmPlanChange = async () => {
    if (!pendingChange) return;
    setChangeLoading(true);
    try {
      const r = await api.post('/api/billing/change-plan', { plan_key: pendingChange.plan.plan_key });
      if (r.data?.requires_action && r.data?.hosted_invoice_url) {
        // SCA/3DS: el banco requiere autenticación. Llevamos al cliente a la factura
        // alojada de Stripe para completar el pago; al volver, la página reconcilia el plan.
        toast.info('Tu banco requiere confirmar el pago. Te llevamos a completarlo de forma segura...');
        setTimeout(() => { window.location.href = r.data.hosted_invoice_url; }, 1200);
        return;
      }
      if (r.data?.changed) {
        await loadStatus();
        await refreshFeatures();
        toast.success(`Plan cambiado a ${pendingChange.plan.name}. Se ha cobrado solo la diferencia prorrateada.`);
      } else if (r.data?.reason === 'same_plan') {
        toast.info('Ya tienes ese plan.');
      }
      setPendingChange(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.code === 'no_active_subscription'
        ? 'No tienes una suscripción activa. Usa el checkout.'
        : 'No se pudo cambiar el plan.');
      toast.error(msg);
    } finally {
      setChangeLoading(false);
    }
  };

  const isActive = status?.subscription_status === 'active';
  const currentPlan = status?.plan_key;
  const searchesUsed = status?.searches_this_month ?? 0;
  const searchesMax = status?.max_searches_per_month;
  const agentsLimit = status?.agents_limit ?? 0;
  const agentsBaseLimit = status?.agents_base_limit ?? 0;
  const extraAgentsCount = status?.extra_agents_count ?? 0;

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-white mb-2">Facturación</h1>
      <p className="text-gray-400 mb-8">Gestiona tu suscripción y límites de uso.</p>

      {/* Banner de estado post-checkout */}
      {syncMessage && (
        <div className={`mb-6 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3 ${
          syncMessage.type === 'success' ? 'bg-emerald-900/40 border border-emerald-500/40 text-emerald-300' :
          syncMessage.type === 'warn'    ? 'bg-yellow-900/40 border border-yellow-500/40 text-yellow-300' :
                                           'bg-blue-900/40 border border-blue-500/40 text-blue-300'
        }`}>
          <span>{syncMessage.text}</span>
          <button onClick={() => setSyncMessage(null)} className="opacity-60 hover:opacity-100 text-lg leading-none">&times;</button>
        </div>
      )}

      {/* Billing details form */}
      <BillingDetailsForm />

      {/* Current status */}
      {!loading && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Plan actual</p>
              <p className="text-white text-xl font-bold mt-1" data-testid="current-plan-name">
                {status?.plan?.name || (currentPlan ? currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1) : 'Sin plan')}
              </p>
              <span className={`inline-block mt-2 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isActive ? 'bg-emerald-500/20 text-emerald-400' :
                status?.subscription_status === 'trial' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-gray-700 text-gray-400'
              }`}>
                {isActive ? 'Activo' : status?.subscription_status === 'trial' ? 'Trial' : 'Inactivo'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => reconcile({ silent: false })}
                disabled={reconciling}
                data-testid="reconcile-subscription-btn"
                title="Sincroniza tu plan con la suscripción real de Stripe (útil si el pago no se reflejó)"
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-200 px-3 py-2 rounded-lg text-sm disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${reconciling ? 'animate-spin' : ''}`} />
                {reconciling ? 'Sincronizando...' : 'Sincronizar'}
              </button>
              {isActive && (
                <button
                  onClick={handlePortal}
                  data-testid="manage-subscription-btn"
                  className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm"
                >
                  Gestionar suscripción
                </button>
              )}
            </div>
          </div>

          {/* Searches usage bar */}
          {isActive && searchesMax && (
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Búsquedas este mes</span>
                <span className="text-white">
                  {searchesUsed.toLocaleString()} / {searchesMax.toLocaleString()}
                </span>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full transition-all"
                  style={{ width: `${Math.min((searchesUsed / searchesMax) * 100, 100)}%` }}
                />
              </div>
              {searchesUsed >= searchesMax * 0.8 && (
                <p className="text-yellow-400 text-xs mt-2">
                  Estás usando el {Math.round((searchesUsed / searchesMax) * 100)}% de tu plan. Considera hacer upgrade.
                </p>
              )}
            </div>
          )}

          {/* Agents usage */}
          {isActive && agentsLimit > 0 && (
            <div className="mt-4" data-testid="billing-agents-usage">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Agentes</span>
                <div className="flex items-center gap-2">
                  <span className="text-white tabular-nums">
                    {agentsBaseLimit} base{extraAgentsCount > 0 && ` + ${extraAgentsCount} extra`}
                    {' · '}<span className="font-semibold">{agentsLimit} total</span>
                  </span>
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Añade agentes extra a tu plan desde la sección{' '}
                <span className="text-amber-400 font-medium">Complementos</span> de abajo.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Plans */}
      <>
        <h2 className="text-white font-semibold mb-4">
          {isActive ? 'Cambiar de plan' : 'Elige tu plan'}
        </h2>
        {plansLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[1,2,3].map(i => (
              <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-6 animate-pulse">
                <div className="h-5 bg-gray-800 rounded w-24 mb-3" />
                <div className="h-8 bg-gray-800 rounded w-20 mb-6" />
                {[1,2,3].map(j => <div key={j} className="h-3 bg-gray-800 rounded w-full mb-2" />)}
              </div>
            ))}
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {plans.map(plan => {
            const meta = PLAN_META[plan.plan_key] || { color: 'gray', popular: false };
            const cls = COLOR_CLASSES[meta.color] || COLOR_CLASSES.gray;
            const isCurrent = currentPlan === plan.plan_key;
            const featureList = planFeaturesToList(plan.features || {});
            return (
              <div
                key={plan.plan_key}
                className={`bg-gray-900 border rounded-xl p-6 relative ${
                  isCurrent ? 'border-emerald-500/60' : meta.popular ? cls.border : 'border-gray-800'
                }`}
              >
                {isCurrent && (
                  <span className="absolute -top-3 left-6 bg-emerald-600 text-white text-xs px-3 py-1 rounded-full font-medium">
                    Plan actual
                  </span>
                )}
                {!isCurrent && meta.popular && (
                  <span className={`absolute -top-3 left-6 text-white text-xs px-3 py-1 rounded-full font-medium ${cls.badge}`}>
                    Más popular
                  </span>
                )}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-white font-bold text-lg">{plan.name}</h3>
                    <p className="text-gray-400 text-sm line-clamp-2">{plan.description || ''}</p>
                  </div>
                  <div className="text-right ml-2 flex-shrink-0">
                    <span className="text-white text-3xl font-bold">{fmtPrice(plan.price)}€</span>
                    <span className="text-gray-400 text-sm">/mes</span>
                  </div>
                </div>
                <ul className="space-y-2 mb-6">
                  {featureList.length === 0 ? (
                    <li className="text-gray-500 text-sm italic">Ver descripción</li>
                  ) : featureList.map(f => (
                    <li key={f} className="flex items-center gap-2 text-gray-300 text-sm">
                      <span className="text-emerald-400 flex-shrink-0">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <button
                    disabled
                    className="w-full py-3 rounded-xl font-medium text-sm bg-emerald-900/40 text-emerald-400 border border-emerald-500/30 cursor-default"
                  >
                    Plan activo
                  </button>
                ) : (
                  <button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={previewLoading === plan.plan_key || checkoutLoading === plan.plan_key}
                    data-testid={`select-plan-${plan.plan_key}`}
                    className={`w-full py-3 rounded-xl font-medium text-sm transition-colors text-white ${cls.btn} disabled:opacity-50`}
                  >
                    {previewLoading === plan.plan_key ? 'Calculando...' :
                      checkoutLoading === plan.plan_key ? 'Redirigiendo...' :
                      isActive ? `Cambiar a ${plan.name} — ${fmtPrice(plan.price)}€/mes` : `Suscribirse — ${fmtPrice(plan.price)}€/mes`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        )}

        {/* Onboarding add-on */}
        {!isActive && (
          <div className="bg-gray-900 border border-yellow-500/20 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-semibold">Add-on: Onboarding personalizado</h3>
                <p className="text-gray-400 text-sm mt-1">Instalación y configuración hecha por nuestro equipo. Pago único.</p>
              </div>
              <div className="text-right">
                <span className="text-white text-2xl font-bold">99€</span>
                <span className="text-gray-400 text-sm"> único</span>
              </div>
            </div>
          </div>
        )}
      </>

      {/* Add-ons */}
      {addons.length > 0 && (
        <>
          <h2 className="text-white font-semibold mb-4 mt-2">Complementos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {addons.map(addon => {
              const isRecurring = addon.interval && addon.interval !== 'one_time' && addon.interval !== 'once';
              const supportsQty = addon.plan_key === 'extra_agents';
              const qty = Math.max(1, Number(addonQuantities[addon.plan_key] || 1));
              const totalPrice = fmtPrice(Number(addon.price) * qty);
              return (
              <div key={addon.plan_key} className="bg-gray-900 border border-gray-800 rounded-xl p-5 flex flex-col gap-3">
                <div>
                  <span className="text-xs font-medium text-amber-400 uppercase tracking-wide">Complemento</span>
                  <h3 className="text-white font-bold text-base mt-0.5">{addon.name}</h3>
                  <p className="text-gray-400 text-xs mt-1">{addon.description}</p>
                  {addon.plan_key === 'extra_agents' && isActive && agentsLimit > 0 && (
                    <p className="text-violet-400 text-xs mt-1.5" data-testid="extra-agents-current-note">
                      Tu plan incluye <strong>{agentsBaseLimit}</strong> agente{agentsBaseLimit !== 1 ? 's' : ''} base
                      {extraAgentsCount > 0 && ` + ${extraAgentsCount} extra ya contratado${extraAgentsCount !== 1 ? 's' : ''}`}.
                    </p>
                  )}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-white text-2xl font-bold">{totalPrice}€</span>
                  <span className="text-gray-400 text-sm">
                    {isRecurring ? '/mes' : ' único'}
                    {supportsQty && qty > 1 && ` (${fmtPrice(addon.price)}€ × ${qty})`}
                    {supportsQty && qty === 1 && ' · por agente'}
                  </span>
                </div>
                {supportsQty && (
                  <div className="flex items-center gap-2 bg-gray-950 border border-gray-800 rounded-lg px-2 py-1.5">
                    <span className="text-gray-400 text-xs flex-1">Cantidad</span>
                    <button
                      data-testid={`addon-qty-decrement-${addon.plan_key}`}
                      onClick={() => setAddonQuantities(q => ({ ...q, [addon.plan_key]: Math.max(1, (q[addon.plan_key] || 1) - 1) }))}
                      className="w-7 h-7 rounded bg-gray-800 hover:bg-gray-700 text-white text-lg font-bold flex items-center justify-center disabled:opacity-40"
                      disabled={qty <= 1}
                    >−</button>
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={qty}
                      onChange={(e) => setAddonQuantities(q => ({ ...q, [addon.plan_key]: Math.max(1, Math.min(99, Number(e.target.value) || 1)) }))}
                      className="w-12 text-center bg-gray-900 border border-gray-800 rounded text-white text-sm py-1"
                      data-testid={`addon-qty-input-${addon.plan_key}`}
                    />
                    <button
                      data-testid={`addon-qty-increment-${addon.plan_key}`}
                      onClick={() => setAddonQuantities(q => ({ ...q, [addon.plan_key]: Math.min(99, (q[addon.plan_key] || 1) + 1) }))}
                      className="w-7 h-7 rounded bg-gray-800 hover:bg-gray-700 text-white text-lg font-bold flex items-center justify-center"
                    >+</button>
                  </div>
                )}
                <button
                  onClick={() => handleCheckout(addon.plan_key, supportsQty ? qty : 1)}
                  disabled={checkoutLoading === addon.plan_key}
                  data-testid={`addon-checkout-${addon.plan_key}`}
                  className="w-full py-2 rounded-lg text-sm font-medium bg-amber-500 hover:bg-amber-400 text-black transition-colors disabled:opacity-50"
                >
                  {checkoutLoading === addon.plan_key
                    ? 'Redirigiendo...'
                    : (supportsQty
                        ? `Añadir ${qty} agente${qty !== 1 ? 's' : ''} (+${totalPrice}€/mes)`
                        : `Contratar (${fmtPrice(addon.price)}€${isRecurring ? '/mes' : ''})`)}
                </button>
              </div>
              );
            })}
          </div>
        </>
      )}

      {/* Modal de confirmación de cambio de plan con prorrateo */}
      {pendingChange && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          data-testid="change-plan-modal"
        >
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-white text-lg font-bold mb-2">Cambiar al plan {pendingChange.plan.name}</h3>

            {pendingChange.preview?.available ? (
              (() => {
                const amt = pendingChange.preview.amount_due_now;
                const cur = pendingChange.preview.currency || 'EUR';
                const isCharge = amt > 0;
                const isCredit = amt < 0;
                return (
                  <div className="text-sm text-gray-300 space-y-3 mb-5">
                    {isCharge && (
                      <p data-testid="proration-charge">
                        Se te cobrará ahora <span className="text-white font-bold">{fmtPrice(amt)} {cur}</span> por la diferencia
                        prorrateada de los días que quedan del periodo actual.
                      </p>
                    )}
                    {isCredit && (
                      <p data-testid="proration-credit">
                        Se generará un <span className="text-emerald-400 font-bold">crédito de {fmtPrice(Math.abs(amt))} {cur}</span> que
                        se aplicará a tus próximas facturas.
                      </p>
                    )}
                    {!isCharge && !isCredit && (
                      <p>No hay diferencia a cobrar ahora por este cambio.</p>
                    )}
                    <p className="text-gray-400">
                      A partir de la próxima renovación se cobrará el precio completo del plan:
                      <span className="text-white font-semibold"> {fmtPrice(pendingChange.plan.price)}€/mes</span>.
                    </p>
                  </div>
                );
              })()
            ) : (
              <div className="text-sm text-gray-300 space-y-3 mb-5">
                <p>
                  Vas a cambiar al plan <span className="text-white font-semibold">{pendingChange.plan.name}</span>. Stripe cobrará
                  automáticamente solo la <span className="text-white font-semibold">diferencia prorrateada</span> de los días
                  restantes del periodo (o generará un crédito si es un plan inferior).
                </p>
                <p className="text-gray-400">
                  La próxima renovación será de <span className="text-white font-semibold">{fmtPrice(pendingChange.plan.price)}€/mes</span>.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setPendingChange(null)}
                disabled={changeLoading}
                data-testid="cancel-change-plan-btn"
                className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-gray-800 hover:bg-gray-700 text-gray-200 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmPlanChange}
                disabled={changeLoading}
                data-testid="confirm-change-plan-btn"
                className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {changeLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {changeLoading ? 'Aplicando...' : 'Confirmar cambio'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
